import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

export interface KycDocumentItem {
  id: number;
  userId: number;
  email: string;

  documentType: string;
  documentSide: string;
  status: string;
  source: string;

  fileName: string;
  mimeType: string;
  fileSize: number;

  reviewerId?: number;
  reviewerEmail?: string;

  kycStatus: string;
  riskLevel: string;

  createdAt: string;
  updatedAt: string;
}

export interface RiskKycDocumentOption {
  value: string;
  title: string;
}

interface RiskKycDocumentsOptions {
  documentTypes: RiskKycDocumentOption[];
  documentSides: RiskKycDocumentOption[];
  documentStatuses: RiskKycDocumentOption[];
}

interface GetRiskKycDocumentsPayload {
  limit: number;
  offset: number;

  email?: string;
  userIds?: number[];

  documentType?: string[];
  documentSide?: string;

  status?: string[];
  source?: string;

  order?: {
    id?: string;
    createdAt?: string;
    updatedAt?: string;
    status?: string;
    documentType?: string;
    documentSide?: string;
  };
}

interface UploadRiskKycDocumentPayload {
  userId: number;
  documentType: string;
  documentSide: string;
  file: File;
}

interface ReviewRiskKycDocumentPayload {
  documentId: number;
  status: "pending" | "approved" | "rejected" | "expired" | "requiresReview";
}

interface RiskKycDocumentsState {
  list: KycDocumentItem[];

  options?: RiskKycDocumentsOptions;

  activePage: number;

  totalItems: number;
  totalPages: number;

  isListFetched: boolean;
  isOptionsFetched: boolean;
  isUploading: boolean;
  processingDocumentId?: number;
}

const initialState: RiskKycDocumentsState = {
  list: [],

  options: undefined,

  activePage: 1,

  totalItems: 0,
  totalPages: 0,

  isListFetched: false,
  isOptionsFetched: false,
  isUploading: false,
};

export const getRiskKycDocumentsOptions = createAsyncThunk(
  "kyc-documents/options",
  async () => {
    try {
      const result = await instance.get(apiRoutes.riskKycDocumentsOptions);

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getRiskKycDocumentsList = createAsyncThunk(
  "kyc-documents/list",
  async (data: GetRiskKycDocumentsPayload) => {
    try {
      const result = await instance.post(apiRoutes.riskKycDocumentsList, data);

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getRiskKycDocumentsByUser = createAsyncThunk(
  "kyc-documents/by-user",
  async (userId: number) => {
    try {
      const result = await instance.get(
        apiRoutes.riskKycDocumentsByUser(userId),
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const uploadRiskKycDocument = createAsyncThunk(
  "kyc-documents/upload",
  async (data: UploadRiskKycDocumentPayload, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      formData.append("documentType", data.documentType);
      formData.append("documentSide", data.documentSide);
      formData.append("file", data.file);

      const result = await instance.post(
        apiRoutes.riskKycDocumentsUpload(data.userId),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return result.data;
    } catch (error: any) {
      const responseData = error?.data ?? error?.response?.data;
      const message = responseData?.error?.message ?? responseData?.message;

      return rejectWithValue({
        code: "BusinessError",
        fieldErrors: [],
        globalErrors: [{ code: message ?? "unknownError" }],
      });
    }
  },
);

export const reviewRiskKycDocument = createAsyncThunk(
  "kyc-documents/review",
  async (data: ReviewRiskKycDocumentPayload, { rejectWithValue }) => {
    try {
      const result = await instance.put(
        apiRoutes.riskKycDocumentReview(data.documentId),
        { status: data.status },
      );

      return result.data;
    } catch (error: any) {
      const responseData = error?.data ?? error?.response?.data;
      const message = responseData?.error?.message ?? responseData?.message;
      const errors = responseData?.errors;

      return rejectWithValue({
        code: "BusinessError",
        fieldErrors: [],
        globalErrors: Array.isArray(errors)
          ? errors.map((item) => ({ code: item.message }))
          : [{ code: message ?? "unknownError" }],
      });
    }
  },
);

export const riskKycDocumentsSlice = createSlice({
  name: "risk-kyc-documents",

  initialState,

  reducers: {
    handlePaginate: (
      state: RiskKycDocumentsState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;

      state.isListFetched = false;
    },

    handleClearList: (state: RiskKycDocumentsState) => {
      state.list = [];

      state.activePage = 1;

      state.totalItems = 0;
      state.totalPages = 0;

      state.isListFetched = false;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getRiskKycDocumentsOptions.fulfilled, (state, action) => {
      state.options = action.payload;

      state.isOptionsFetched = true;
    });

    builder.addCase(getRiskKycDocumentsList.fulfilled, (state, action) => {
      state.list = action.payload?.items ?? [];

      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 0;

      state.isListFetched = true;
    });
    builder.addCase(getRiskKycDocumentsList.rejected, (state) => {
      state.isListFetched = true;
    });

    builder.addCase(getRiskKycDocumentsOptions.rejected, (state) => {
      state.isOptionsFetched = true;
    });
    builder.addCase(uploadRiskKycDocument.pending, (state) => {
      state.isUploading = true;
    });

    builder.addCase(uploadRiskKycDocument.fulfilled, (state) => {
      state.isUploading = false;
    });

    builder.addCase(uploadRiskKycDocument.rejected, (state) => {
      state.isUploading = false;
    });
    builder.addCase(getRiskKycDocumentsByUser.fulfilled, (state, action) => {
      state.list = action.payload?.items ?? [];

      state.isListFetched = true;
    });
    builder.addCase(getRiskKycDocumentsByUser.rejected, (state) => {
      state.isListFetched = true;
    });
    builder.addCase(getRiskKycDocumentsByUser.pending, (state) => {
      state.isListFetched = false;
    });
    builder.addCase(reviewRiskKycDocument.pending, (state, action) => {
      state.processingDocumentId = action.meta.arg.documentId;
    });
    builder.addCase(reviewRiskKycDocument.fulfilled, (state, action) => {
      state.processingDocumentId = undefined;

      const updatedDocument = action.payload;

      if (!updatedDocument) return;

      const documentIndex = state.list.findIndex(
        (document) => document.id === updatedDocument.id,
      );

      if (documentIndex !== -1) {
        state.list[documentIndex] = {
          ...state.list[documentIndex],
          ...updatedDocument,
        };
      }
    });
    builder.addCase(reviewRiskKycDocument.rejected, (state) => {
      state.processingDocumentId = undefined;
    });
  },
});

export const { handlePaginate, handleClearList } =
  riskKycDocumentsSlice.actions;
